import React, { useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { PROVIDER_SECTION_BREADCRUMB } from './provider-breadcrumb'
import { useApiListProvider } from '../../../../../api-new/state/auth'
import { IconPolicyFinding } from '../../../../../shared/icon/icon'
import { useLoad } from '../../../../../shared/hooks/load'
import { FleetPage, FleetSection } from '../../../../../shared/sso-workspace/fleet-page'
import { FleetGrid, useGridList } from '../../../../../shared/sso-workspace/fleet-grid'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu } from '../../../../../shared/table-builder/table-columns'
import { Menu, MenuDetails } from '../../../../../shared/menu/menu'
import { Pill } from '../../../../../shared/sso-workspace/pill'
import { authGet } from '../../../../../shared/sso-workspace/auth-api'
import { formatAgo, formatDateTime, serverMessage } from '../../../../../shared/sso-workspace/format'
import { FINDING_RULE_OPTIONS, detailPairs, findingRule } from '../../../../../shared/sso-workspace/finding-rules'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { SEARCH_FIELD_PROPS } from '../../../../../shared/component/search-field-props'

export const PAGE_SIZE = 100

const SINCE_OPTIONS = [
  { value: '', label: 'Any time' },
  { value: '1', label: 'Last 24 hours' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' }
]

const OUTCOME_OPTIONS = [
  { value: '', label: 'Blocked or reported' },
  { value: 'true', label: 'Blocked' },
  { value: 'false', label: 'Reported only' }
]

const sinceDate = days => (days ? new Date(Date.now() - Number(days) * 24 * 60 * 60 * 1000).toISOString() : '')

// Report-only mode (spec 3.3, 3.6, 17.1) records what a rule WOULD have
// rejected; enforced rules record what they did. In the workspace's
// language (audit F8): each finding names its connection (never a bare id)
// and links to it, says the rule in plain words with what it means and
// what to do, and shows its detail as labelled values. Filters by
// connection, rule, email, time and outcome run on the server, and "Load
// older findings" pages back through everything with a cursor instead of
// stopping at the newest 200. ?providerID= opens it on one connection
// (the workspace's Sign-in activity links here).
//
// The list is the table-builder's AG Grid: the server filters and pages, so
// the grid is keyed on the filters (a change remounts it and its onReady
// loads the first page) and "Load older findings" appends the next page.
// A finding's explanation opens in the panel under the grid (row click or
// its details action), the way provider-health shows a row's detail.
export const PolicyFindingList = () => {
  const { search } = useLocation()
  const initialProvider = useMemo(() => new URLSearchParams(search).get('providerID') || '', []) // eslint-disable-line react-hooks/exhaustive-deps
  const apiListProvider = useApiListProvider()

  const [providers, setProviders] = useState([])
  const [filters, setFilters] = useState(NO_FILTERS_WITH(initialProvider))
  const [emailText, setEmailText] = useState('')
  const [rows, setRows] = useState(undefined)
  const [more, setMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const [selectedID, setSelectedID] = useState(null)

  const tableRef = useRef()

  // Every load takes a sequence number; an answer that is no longer current
  // (the filters changed while it was in flight) is dropped.
  const seq = useRef(0)

  useLoad(async () => {
    try { setProviders((await apiListProvider.get()) || []) } catch (err) { setProviders([]) }
  }, [])

  const params = before => ({
    limit: PAGE_SIZE,
    providerID: filters.providerID,
    rule: filters.rule,
    email: filters.email,
    since: sinceDate(filters.since),
    enforced: filters.enforced,
    before
  })

  // The grid's onReady (and its footer's reload): the first page for the
  // current filters. A filter change remounts the grid (it is keyed on the
  // filters), so the new grid's onReady loads the new first page.
  const load = async () => {
    const mine = ++seq.current
    setError(null)
    try {
      const page = (await authGet('auth-policy-finding', params())) || []
      if (mine !== seq.current) return
      setRows(page)
      setMore(page.length === PAGE_SIZE)
    } catch (err) {
      if (mine !== seq.current) return
      setRows(null)
      setError(serverMessage(err, "Findings couldn't be loaded."), err)
    }
  }

  const loadOlder = async () => {
    if (!rows || !rows.length) return
    const mine = seq.current
    setLoadingMore(true)
    try {
      const page = (await authGet('auth-policy-finding', params(rows[rows.length - 1].id))) || []
      if (mine !== seq.current) return
      setRows(r => (Array.isArray(r) ? [...r, ...page] : r))
      setMore(page.length === PAGE_SIZE)
    } catch (err) {
      if (mine !== seq.current) return
      setError(serverMessage(err, "Older findings couldn't be loaded."), err)
    } finally {
      if (mine === seq.current) setLoadingMore(false)
    }
  }

  const changeFilters = next => {
    setRows(undefined)
    setMore(false)
    setSelectedID(null)
    setFilters(next)
  }

  const set = (key, value) => changeFilters({ ...filters, [key]: value })
  const filtered = Object.values(filters).some(Boolean)
  const clear = () => { setEmailText(''); changeFilters(NO_FILTERS_WITH('')) }

  const connectionOptions = useMemo(
    () => providers.map(p => ({ value: String(p.id), label: p.displayName || p.providerName })).sort((a, b) => a.label.localeCompare(b.label)),
    [providers]
  )

  // Display values flattened onto the row, so the grid's quick filter, sort
  // and CSV export work off the words shown.
  const gridRows = useMemo(
    () =>
      Array.isArray(rows)
        ? rows.map(f => ({
            ...f,
            whatHappened: findingRule(f.rule).title,
            connection: f.providerName || (f.providerID ? `Connection ${f.providerID}` : ''),
            organization: f.organizationName || '',
            person: f.email || '',
            outcome: f.enforced ? 'Blocked' : 'Reported only'
          }))
        : rows,
    [rows]
  )
  const gridList = useGridList(gridRows)

  const selected = (rows || []).find(f => f.id === selectedID)

  // Row click selects (it does not toggle): ag-grid dispatches rowClicked
  // through its async event queue, after the Details button's own click.
  const handleSelect = f => f && setSelectedID(f.id)

  const gridKey = JSON.stringify(filters)

  return (
    <BreadcrumbView parent={PROVIDER_SECTION_BREADCRUMB} Icon={IconPolicyFinding} text="FINDINGS">
      <FleetPage
        tabs
        title="Findings"
        purpose="What the sign-in rules blocked, or would have blocked while they only report, newest first. Open one for what it means and what to do.">
        <div className="sw-toolbar">
          <select className="form-select form-select-sm" aria-label="Connection" value={filters.providerID} onChange={e => set('providerID', e.target.value)}>
            <option value="">Every connection</option>
            {connectionOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className="form-select form-select-sm" aria-label="What happened" value={filters.rule} onChange={e => set('rule', e.target.value)}>
            <option value="">Every rule</option>
            {FINDING_RULE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <form className="d-flex gap-1 sw-search" role="search" onSubmit={e => { e.preventDefault(); set('email', emailText.trim()) }}>
            <input {...SEARCH_FIELD_PROPS} name="finding-search" className="form-control form-control-sm" placeholder="Email contains…" aria-label="Email contains"
              value={emailText} onChange={e => { setEmailText(e.target.value); if (!e.target.value && filters.email) set('email', '') }} />
            <button type="submit" className="btn btn-sm sw-btn">Find</button>
          </form>
          <select className="form-select form-select-sm" aria-label="When" value={filters.since} onChange={e => set('since', e.target.value)}>
            {SINCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className="form-select form-select-sm" aria-label="Outcome" value={filters.enforced} onChange={e => set('enforced', e.target.value)}>
            {OUTCOME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {error && <p className="text-danger small" role="alert">{error}</p>}
        {error && <ErrorReference error={failure} />}
        <FleetGrid title="findings" tableRef={tableRef} exportName="findings">
          <TableAgClient
            key={gridKey}
            ref={tableRef}
            apiList={gridList}
            onReady={load}
            onRowClicked={handleSelect}
            rowHeight={ROW_HEIGHT_TWO_LINE}
            sortCol="createdAt"
            sortDesc
            emptyState={{
              Icon: IconPolicyFinding,
              title: filtered ? 'No matching findings' : 'No findings yet',
              message: filtered ? 'No findings match these filters.' : 'No findings yet. When a sign-in rule blocks someone, or would have, it shows up here.',
              action: { label: 'Clear filters', onClick: clear, show: filtered }
            }}>
            <ColumnFormat field="createdAt" header="When" render={agoCell} sortable />
            <ColumnFormat field="whatHappened" header="What happened" render={ruleCell} stacked minWidth={220} flex={2} sortable />
            <ColumnFormat field="connection" header="Connection" render={connectionCell} sortable />
            <ColumnFormat field="organization" header="Organization" render={noneCell} sortable />
            <ColumnFormat field="person" header="Person" render={unknownCell} sortable />
            <ColumnFormat field="outcome" header="Outcome" render={outcomeCell} sortable />
            <ColumnMenu
              menu={f =>
                <Menu small>
                  <MenuDetails onClick={() => setSelectedID(f.id)} show />
                </Menu>}
            />
          </TableAgClient>
        </FleetGrid>
        {more &&
          <div className="d-flex justify-content-end mb-3">
            <button type="button" className="btn btn-sm sw-btn" disabled={loadingMore} onClick={loadOlder}>{loadingMore ? 'Loading…' : 'Load older findings'}</button>
          </div>}
        <FleetSection id="finding-detail" title={selected ? findingRule(selected.rule).title : 'Finding'}
          actions={selected && <button type="button" className="btn btn-sm sw-btn" onClick={() => setSelectedID(null)}>Close</button>}>
          {selected
            ? <FindingDetail finding={selected} />
            : <p className="sw-hint m-0">Choose details on a finding to see what it means and what to do.</p>}
        </FleetSection>
      </FleetPage>
    </BreadcrumbView>
  )
}

const NO_FILTERS_WITH = providerID => ({ providerID, rule: '', email: '', since: '', enforced: '' })

// What a finding means, what to do, and its detail as labelled values.
const FindingDetail = ({ finding: f }) => {
  const rule = findingRule(f.rule)
  const pairs = detailPairs(f.detail)
  return (
    <div className="d-grid gap-3 py-1" data-testid={`detail-${f.id}`}>
      {rule.meaning && <p className="m-0">{rule.meaning}</p>}
      {rule.fix && <p className="m-0"><span className="fw-semibold">What to do: </span>{rule.fix}</p>}
      <dl className="sw-dl">
        <dt>Recorded</dt><dd>{formatDateTime(f.createdAt)}</dd>
        {pairs.map(p => <React.Fragment key={p.key}><dt>{p.label}</dt><dd className="sw-mono">{p.text}</dd></React.Fragment>)}
      </dl>
    </div>
  )
}

const agoCell = row => (row.value ? <span title={formatDateTime(row.value)}>{formatAgo(row.value)}</span> : '')

const ruleCell = row => (row.data ? <span>{row.value}<span className="sw-cell-sub sw-mono">{row.data.rule}</span></span> : '')

const connectionCell = row =>
  row.data
    ? row.data.providerID
      ? <Link to={`/admin/provider/list/edit/${row.data.providerID}`}>{row.value}</Link>
      : <span className="sw-hint">None</span>
    : ''

const noneCell = row => row.value || <span className="sw-hint">None</span>

const unknownCell = row => row.value || <span className="sw-hint">Unknown</span>

const outcomeCell = row => (row.data ? <Pill tone={row.data.enforced ? 'bad' : null}>{row.value}</Pill> : '')
